import { Component, Input, OnInit, Output, EventEmitter, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DonationCardService } from '../../../services/donation-card.service';
import { DonationNotesViewModel } from '../../../models/DonationCardModels';
import { GenericButtonComponent } from '../../../../global/component/generic/generic-button/generic-button.component';
import { InputSwitchModule } from 'primeng/inputswitch';
import { CalendarModule } from 'primeng/calendar';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';

@Component({
  selector: 'app-donation-notes-tab',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextareaModule,
    GenericButtonComponent,
    InputSwitchModule,
    CalendarModule,
    ToastModule
  ],
  templateUrl: './donation-notes-tab.component.html',
  styleUrl: './donation-notes-tab.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [MessageService]
})
export class DonationNotesTabComponent implements OnInit {
  @Input() donationId!: number;

  @Input() thankYouNoteDialogVisible = false;
  @Input() customNoteDialogVisible = false;
  @Input() isReadOnlyMode: boolean = false;

  notes?: DonationNotesViewModel;
  customNoteText: string = ''; 
  isThankYouNoteSent: boolean = false;
  thankYouNoteSentDate?: Date;
  showThankYouNoteSentDateError: boolean = false;

  constructor(private service: DonationCardService, private messageService: MessageService) { }

  ngOnInit(): void {
    this.service.getNotes(this.donationId).subscribe(data => {
      this.notes = data;
      this.isThankYouNoteSent = data.isThankYouNoteSent;
      this.thankYouNoteSentDate = data.thankYouNoteSentDate ? new Date(data.thankYouNoteSentDate) : undefined;
    });
  }

  onOpenThankYouDialog(): void {
    this.service.getNotes(this.donationId).subscribe(data => {
      this.thankYouNoteDialogVisible = true;
      this.notes = data;
      this.isThankYouNoteSent = data.isThankYouNoteSent;
      this.thankYouNoteSentDate = data.thankYouNoteSentDate ? new Date(data.thankYouNoteSentDate) : undefined;
    });
    
  }

  bindNotesToEditModel(): void {
    if (this.notes) {
      this.customNoteText = this.notes.customNote || '';
      this.isThankYouNoteSent = this.notes.isThankYouNoteSent;
      this.thankYouNoteSentDate = this.notes.thankYouNoteSentDate ? new Date(this.notes.thankYouNoteSentDate) : undefined;
    }
  }

  onOpenCustomNoteDialog(): void {
    this.customNoteDialogVisible = true;
    this.bindNotesToEditModel(); 
  }

  saveCustomNote(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }
    this.service.saveCustomNote({ donationId: this.donationId, note: this.customNoteText }).subscribe({
      next: () => {
        this.notes!.customNote = this.customNoteText; 
        this.customNoteDialogVisible = false; 
        this.customNoteText = '';
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Custom note updated successfully.' }); 
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update custom note. Please try again.' }); 
        console.error('Error saving custom note:', err); 
      }
    });
  }

  saveThankYouNote(): void {
    if (this.isReadOnlyMode) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Read Only',
        detail: 'This tab is in read-only mode. Saving is disabled.'
      });
      return;
    }
    if (this.isThankYouNoteSent && !this.thankYouNoteSentDate) {
      this.showThankYouNoteSentDateError = true;
      return;
    } else {
      this.showThankYouNoteSentDateError = false;
    }

    const payload = {
      isThankYouNoteSent: this.isThankYouNoteSent,
      thankYouNoteSentDate: this.thankYouNoteSentDate,
    };

    this.service.saveThankYouNote(this.donationId, payload).subscribe({
      next: () => {
        this.notes!.isThankYouNoteSent = this.isThankYouNoteSent;
        this.notes!.thankYouNoteSentDate = this.thankYouNoteSentDate ? this.thankYouNoteSentDate.toISOString() : undefined;
        this.thankYouNoteDialogVisible = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Thank you note updated successfully.' });
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update thank you note. Please try again.' });
        console.error('Error saving thank you note:', err);
      },
    });
  }

  onToggleThankYouNote(event: any): void {
    this.isThankYouNoteSent = event.checked; 
    if (!this.isThankYouNoteSent) {
      this.thankYouNoteSentDate = undefined; 
    }
  }

  onCancelCustomNote(): void {
    this.customNoteText = '';
    this.customNoteDialogVisible = false;
  }

  onCancelThankyouNote(): void {
    this.thankYouNoteDialogVisible = false;
    this.resetThankyouNoteForm();
  }

  resetThankyouNoteForm(): void {
    this.customNoteText = '';
    this.isThankYouNoteSent = false;
    this.thankYouNoteSentDate = undefined;
  }
}
